import { Component } from '@theme/component';
import { clamp, prefersReducedMotion } from '@theme/utilities';

/**
 * Scroll-linked parallax for a scrolling background image behind foreground content.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} background
 * @property {HTMLElement} mediaTrack
 *
 * @extends {Component<Refs>}
 */
class ScrollParallaxComponent extends Component {
  requiredRefs = ['background', 'mediaTrack'];

  /** @type {boolean} */
  #ticking = false;

  /** @type {() => void} */
  #onScroll = () => {
    if (this.#ticking) return;
    this.#ticking = true;

    requestAnimationFrame(() => {
      this.#updateParallax();
      this.#ticking = false;
    });
  };

  connectedCallback() {
    super.connectedCallback();

    this.#updateParallax();

    if (prefersReducedMotion()) {
      this.setAttribute('data-reduced-motion', '');
      return;
    }

    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onScroll, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onScroll);
    super.disconnectedCallback();
  }

  #getParallaxSpeed() {
    const styles = getComputedStyle(this);
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const speed = isMobile ? styles.getPropertyValue('--parallax-speed-mobile') : styles.getPropertyValue('--parallax-speed');
    const parsed = parseFloat(speed);

    return Number.isFinite(parsed) ? parsed : 0.35;
  }

  #updateParallax() {
    const { mediaTrack, background } = this.refs;
    if (!mediaTrack || !background) return;

    if (prefersReducedMotion()) {
      mediaTrack.style.transform = '';
      return;
    }

    const rect = this.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // Only animate while the section is near the viewport.
    if (rect.bottom < 0 || rect.top > viewportHeight) {
      mediaTrack.style.transform = '';
      return;
    }

    // Progress from 0 -> 1 as the section moves through the viewport.
    // Using (rect.height + viewportHeight) yields a stable mapping even for short sections.
    const total = Math.max(rect.height + viewportHeight, 1);
    const progress = clamp((viewportHeight - rect.top) / total, 0, 1);

    // Never move more than the available overflow, otherwise you can reveal the background behind the image.
    const mediaOverflow = Math.max(mediaTrack.offsetHeight - background.offsetHeight, 0);
    if (mediaOverflow <= 0) {
      mediaTrack.style.transform = '';
      return;
    }

    const speed = this.#getParallaxSpeed(); // 0..1-ish; lower = slower movement
    const translateY = progress * mediaOverflow * clamp(speed, 0, 1);

    mediaTrack.style.transform = `translate3d(0, ${-translateY}px, 0)`;
  }
}

if (!customElements.get('scroll-parallax-component')) {
  customElements.define('scroll-parallax-component', ScrollParallaxComponent);
}
